import { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { EasyClaimAppState } from 'src/easyclaim.api.client.typescript/src';
import { reportError } from 'src/easyclaim.app.libs/reportError';
import { useContinueButtonStore } from 'src/easyclaim.app.stores/useContinueButtonStore';
import styled from 'styled-components';

import { Spinner } from '../easyclaim.app.components';

import ButtonConfiguration from './ButtonConfiguration';
import ElementConfiguration from './ElementConfiguration';

export interface AppFooterProps {
	readonly isExecutingAction: boolean;
	readonly spinnerColor: string;
	readonly BackButtonConfiguration: ButtonConfiguration;
	readonly SkipButtonConfiguration: ButtonConfiguration;
	readonly PoweredByConfiguration: ElementConfiguration;
	readonly CurrentScreen: EasyClaimAppState;
}

export function AppBottomNavigation(props: AppFooterProps) {
	const isVisible = useContinueButtonStore((s) => s.isVisible);
	const isStoreDisabled = useContinueButtonStore((s) => s.isDisabled || s.validationDisabled);
	const isBusy = useContinueButtonStore((s) => s.busyCount > 0);
	const isBusyExemptScreen =
		props.CurrentScreen === EasyClaimAppState.DamageImages ||
		props.CurrentScreen === EasyClaimAppState.AdditionalDamagePictures;
	const isDisabled = isStoreDisabled || (isBusy && !isBusyExemptScreen);
	const textId = useContinueButtonStore((s) => s.textId);
	const onClick = useContinueButtonStore((s) => s.onClick);
	const spinnerStyle = useMemo(() => {
		return {
			size: '0.5em',
			margin: '0',
			color: props.spinnerColor,
		};
	}, [props.spinnerColor]);

	const handleContinueClick = useCallback(() => {
		const executeAsync = async () => {
			if (props.CurrentScreen === EasyClaimAppState.Review && document.fullscreenElement) {
				await document.exitFullscreen();
			}

			await onClick();
		};

		executeAsync().catch((error) => {
			reportError(error, { context: 'AppBottomNavigation.handleContinueClick' });
		});
	}, [onClick, props.CurrentScreen]);

	const handleBackClick = useCallback(() => {
		props.BackButtonConfiguration.onClick().catch((error) => {
			reportError(error, { context: 'AppBottomNavigation.handleBackClick' });
		});
	}, [props.BackButtonConfiguration]);

	const handleSkipClick = useCallback(() => {
		props.SkipButtonConfiguration.onClick().catch((error) => {
			reportError(error, { context: 'AppBottomNavigation.handleSkipClick' });
		});
	}, [props.SkipButtonConfiguration]);

	return (
		<BottomContainer className={`main-bottom-nav main-bottom-nav-${props.CurrentScreen}`}>
			{props.BackButtonConfiguration.isVisible && (
				<button
					className="bottom-nav-back-button main-bottom-nav-button"
					onClick={handleBackClick}
					disabled={props.BackButtonConfiguration.disabled}
				>
					« <FormattedMessage id={props.BackButtonConfiguration.textId} />
				</button>
			)}
			{isVisible && (
				<button
					onClick={handleContinueClick}
					disabled={isDisabled}
					className="move-next main-bottom-nav-button"
				>
					{props.isExecutingAction ? (
						<Spinner dotStyle={spinnerStyle} className="move-next-spinner" />
					) : null}
					<FormattedMessage id={textId} />
				</button>
			)}
			{props.SkipButtonConfiguration.isVisible && (
				<button
					onClick={handleSkipClick}
					disabled={props.SkipButtonConfiguration.disabled}
					className="main-bottom-nav-button"
				>
					<FormattedMessage id={props.SkipButtonConfiguration.textId} />
				</button>
			)}
			{props.PoweredByConfiguration.isVisible && (
				<div className="powered-by">
					<FormattedMessage
						id={props.PoweredByConfiguration.textId}
						values={{
							ce: (chunks) => <span className="powered-by-ce">{chunks}</span>,
							solvd: (chunks) => <span className="powered-by-solvd">{chunks}</span>,
						}}
					/>
				</div>
			)}
		</BottomContainer>
	);
}

const BottomContainer = styled.div`
	padding: 1rem;
	flex-grow: 0;
	flex-shrink: 0;
	display: grid;
	grid-row-gap: 2rem;
`;
